import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ADDRESSES } from '@/lib/contracts'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { POLL_SLOW, toNumber } from '@/lib/utils'

/// Pending HORIZON a wallet has accrued on ALM vault shares, one entry per vault it holds.
///
/// Hold-to-earn: entitlement tracks the share BALANCE with no staking step, so there is nothing to
/// read on the vault itself here - `HorizonFarm` is the accountant and owns the whole answer.
///
/// Claiming deliberately does NOT live on the Vaults page. PortfolioSection is this app's single
/// claim surface (see its own doc comment: money-market and farm emissions were consolidated there
/// precisely to stop the "hunt for the claim button on each product page" pattern), and passive
/// vault accrual belongs in that same place. The Vaults page shows the RATE; Portfolio does the
/// claiming. Staking stays the one exception, because its claim is bundled with a
/// claim-vs-compound decision rather than being passive accrual.
export function useVaultEmissions(vaults, user) {
  // Only vaults this wallet actually holds shares in can have anything pending, so don't spend a
  // multicall entry on the rest of the list.
  const held = useMemo(
    () => (vaults || []).filter((v) => !v.loading && v.hasPosition),
    [vaults],
  )

  const { data, refetch } = useReadContracts({
    contracts: held.map((v) => ({
      address: ADDRESSES.HORIZON_EMISSIONS,
      abi: farmAbi,
      functionName: 'pendingVaultHorizonOf',
      args: [v.address, user],
    })),
    query: { enabled: !!user && held.length > 0, refetchInterval: POLL_SLOW },
  })

  return useMemo(() => {
    const positions = held.map((v, i) => ({
      address: v.address,
      name: v.name,
      // A vault registered on the farm but left at zero weight reads 0 here rather than failing -
      // that is the normal state for a freshly created vault, since `createV3Vault.cjs` registers
      // it with no weight and leaves the emission split to a deliberate genesis call.
      pendingHorizon: data?.[i]?.result ?? 0n,
    })).filter((p) => p.pendingHorizon > 0n)

    return {
      positions,
      pendingBase: positions.reduce((s, p) => s + toNumber(p.pendingHorizon), 0),
      refetch,
    }
  }, [held, data, refetch])
}
